"use client";

import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { clsx } from "clsx";

import { Button } from "./Button";
import { useTranslations } from "next-intl";
import { pxToRem } from "@ui/lib/utils";
import { type Key, Tab, TabList, TabListStateContext, TabPanel, Tabs } from "react-aria-components";
import { useContext, useEffect, useState } from "react";
import { HairlineCardBorder } from "./HairlineCardBorder";

export interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  position: number; // Percentage position (0-100) on the timeline
}

// Grid spacing for snapping - uses 9px as the snapping unit
const GRID_SPACING = 9;
const ROUNDED_CONTAINER_SIZE = `round(down, 100%, ${GRID_SPACING - 1}px)`;
const TICK_HEIGHT = 9;
const TICK_HEIGHT_ACTIVE = 124;

function NavigationButton({ direction, className }: { direction: "previous" | "next"; className?: string }) {
  const isPrevious = direction === "previous";
  const tG = useTranslations("ClientKeys.Globals");

  const tabState = useContext(TabListStateContext);
  if (!tabState) return null;

  const prevKey = tabState.selectedKey
    ? (tabState.collection.getKeyBefore(tabState.selectedKey) ?? tabState.collection.getLastKey())
    : null;
  const nextKey = tabState.selectedKey
    ? (tabState.collection.getKeyAfter(tabState.selectedKey) ?? tabState.collection.getFirstKey())
    : null;

  return (
    <Button
      aria-label={isPrevious ? tG("previous") : tG("next")}
      icon={isPrevious ? <ChevronLeft16 /> : <ChevronRight16 />}
      size='pill'
      variant='elevated'
      className={clsx(className, "bg-default hover:bg-default")}
      onClick={() => {
        if (isPrevious) {
          tabState.setSelectedKey(prevKey);
        } else {
          tabState.setSelectedKey(nextKey);
        }
      }}
    />
  );
}

function TimelineMarker({
  event,
  index,
  onHoverStart,
  onHoverEnd,
}: {
  event: TimelineEvent;
  index: number;
  onHoverStart: (event: Key) => void;
  onHoverEnd: (event: Key) => void;
}) {
  const t = useTranslations("ClientKeys.InteractiveTimeline");
  const eventLabel = t("event_label", { number: index + 1, title: event.title, date: event.date });

  return (
    <Tab
      id={event.id}
      aria-label={eventLabel}
      className='tw-group tw-outline-none tw-absolute tw-flex tw-w-px tw-items-center tw-justify-center tw-z-50'
      style={{
        left: `calc(50% - 5px - round(down, ${50 - event.position}%, ${GRID_SPACING}px))`,
        top: `calc(50% - ${TICK_HEIGHT / 2}px)`,
      }}
      onHoverStart={() => onHoverStart(event.id)}
      onHoverEnd={() => onHoverEnd(event.id)}
    >
      {({ isSelected }) => (
        <>
          {/* Main 1px line - always visible */}
          <div
            className='tw-bg-black tw-w-full tw-transition-transform tw-duration-300'
            style={{
              height: TICK_HEIGHT,
              transform: isSelected ? `scale(1.5, ${TICK_HEIGHT_ACTIVE / TICK_HEIGHT})` : "scale(1)",
            }}
          />

          {/* Outer ring - visible when selected */}
          <div
            className={clsx(
              "tw-absolute tw-left-1/2 tw-top-1/2 tw-rounded-full tw-transition-opacity tw-duration-300 group-focus-visible:tw-outline-focus border border-default",
              isSelected ? "tw-opacity-100" : "tw-opacity-0",
            )}
            style={{ width: pxToRem(19), height: pxToRem(142), marginLeft: pxToRem(-9.5), marginTop: pxToRem(-71) }}
          />
        </>
      )}
    </Tab>
  );
}

function TimelineMarkersTrack({
  title,
  events,
  onHoverStart,
  onHoverEnd,
}: {
  title: string;
  events: TimelineEvent[];
  onHoverStart: (event: Key) => void;
  onHoverEnd: (event: Key) => void;
}) {
  // SVG pattern for grid background - creates vertical lines at snapping points
  const gridPattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${GRID_SPACING} ${TICK_HEIGHT}'%3E%3Crect width='0.5' height='${TICK_HEIGHT}' fill='black' opacity='0.12'/%3E%3Crect width='0.5' x='${GRID_SPACING - 0.5}' height='${TICK_HEIGHT}' fill='black' opacity='0.12'/%3E%3C/svg%3E")`;

  return (
    <div
      className='tw-relative tw-w-full tw-grid tw-grid-cols-1 tw-grid-rows-1 tw-items-center'
      style={{ height: pxToRem(142) }}
    >
      {/* Grid background */}
      <div
        className='tw-col-start-1 tw-row-start-1'
        style={{
          width: ROUNDED_CONTAINER_SIZE,
          height: TICK_HEIGHT,
          background: gridPattern,
          backgroundSize: `${GRID_SPACING}px ${TICK_HEIGHT}px`,
          backgroundPosition: "top center",
        }}
      />

      {/* Event markers */}
      <TabList
        aria-label={title}
        className='tw-mx-auto tw-col-start-1 tw-row-start-1'
        style={{ width: ROUNDED_CONTAINER_SIZE }}
      >
        {events.map((event, idx) => (
          <TimelineMarker
            key={event.id}
            event={event}
            index={idx}
            onHoverStart={onHoverStart}
            onHoverEnd={onHoverEnd}
          />
        ))}
      </TabList>
    </div>
  );
}

function DescriptionTrack({ events }: { events: TimelineEvent[] }) {
  return events.map(event => (
    <TabPanel
      key={event.id}
      id={event.id}
      shouldForceMount
      className='tw-group tw-outline-none tw-col-span-2 tw-col-start-1 sm:tw-col-auto sm:tw-col-start-2 tw-row-start-1 tw-row-span-full tw-grid tw-grid-cols-1 tw-grid-rows-[subgrid] tw-relative tw-w-full'
      style={{ width: ROUNDED_CONTAINER_SIZE }}
    >
      {({ isInert }) => (
        <>
          <div
            className={clsx("tw-mx-auto tw-max-w-64 sm:tw-ml-[var(--ml)] tw-w-full tw-text-center")}
            style={
              {
                "--ml": `calc(50% - 5px - round(down, ${50 - event.position}%, ${GRID_SPACING}px) - 8rem)`,
              } as React.CSSProperties
            }
          >
            <h3
              className={clsx(
                "tw-type-base tw-text-black tw-text-balance tw-transition tw-duration-300",
                isInert ? "tw-opacity-0 -tw-translate-y-1" : "tw-opacity-100 tw-delay-100",
              )}
            >
              {event.title}
            </h3>
          </div>
          <div
            className={clsx(
              "tw-mx-auto tw-max-w-64 sm:tw-ml-[var(--ml)] tw-w-full tw-text-center tw-row-start-3 group-focus-visible:tw-outline-focus group-focus-visible:tw-outline-offset-6 tw-rounded",
            )}
            style={
              {
                "--ml": `calc(50% - 5px - round(down, ${50 - event.position}%, ${GRID_SPACING}px) - 8rem)`,
              } as React.CSSProperties
            }
          >
            <p
              className={clsx(
                "tw-type-xs tw-text-black tw-text-balance",
                "tw-transition tw-duration-300",
                isInert ? "tw-opacity-0 tw-translate-y-1" : "tw-opacity-100 tw-delay-150",
              )}
            >
              {event.description}
            </p>
            <p
              className={clsx(
                "tw-mt-4 tw-type-xs tw-text-gray-500",
                "tw-transition tw-duration-300",
                isInert ? "tw-opacity-0 tw-translate-y-1" : "tw-opacity-100 tw-delay-200",
              )}
            >
              {event.date}
            </p>
          </div>
        </>
      )}
    </TabPanel>
  ));
}

export function InteractiveTimelineUI({ title, events }: { title: string; events: TimelineEvent[] }) {
  const [selectedEvent, setSelectedEvent] = useState<Key>(events[events.length - 1].id);
  const [selectedEventPending, setSelectedEventPending] = useState<Key | null>(null);

  useEffect(() => {
    // Delay selection on hover to ensure intent
    if (!selectedEventPending) return;
    const timeout = window.setTimeout(() => {
      setSelectedEvent(selectedEventPending);
    }, 150);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [selectedEventPending]);

  return (
    <div className='tw-relative tw-isolate tw-bg-cream tw-pt-12 tw-pb-10 sm:tw-py-16 lg:tw-py-24 tw-px-8 sm:tw-px-16 tw-rounded-3xl xl:tw-min-h-[31rem] tw-flex tw-items-center'>
      <HairlineCardBorder roundedClassName='tw-rounded-3xl' />
      <Tabs
        selectedKey={selectedEvent}
        onSelectionChange={setSelectedEvent}
        className='tw-w-full tw-max-w-4xl tw-mx-auto tw-grid-rows-[auto_auto_1fr] tw-grid tw-items-center tw-grid-cols-2 sm:tw-grid-cols-[auto_1fr_auto] tw-gap-x-4 sm:tw-gap-x-7 tw-gap-y-4'
      >
        <NavigationButton
          direction='previous'
          className='tw-mt-2 sm:tw-mt-0 tw-row-start-4 sm:tw-row-start-2 tw-ml-auto sm:tw-ml-0'
        />
        <NavigationButton
          direction='next'
          className='tw-mt-2 sm:tw-mt-0 tw-col-start-2 tw-row-start-4 sm:tw-row-start-2 sm:tw-col-start-3'
        />
        <div className='tw-col-start-1 tw-col-span-2 sm:tw-col-auto sm:tw-col-start-2 tw-row-start-2 tw-w-full'>
          <div className='tw-mx-auto' style={{ width: ROUNDED_CONTAINER_SIZE }}>
            <TimelineMarkersTrack
              title={title}
              events={events}
              onHoverStart={id => setSelectedEventPending(id)}
              onHoverEnd={() => setSelectedEventPending(null)}
            />
          </div>
        </div>
        <DescriptionTrack events={events} />
      </Tabs>
    </div>
  );
}
